import React from 'react';
import * as Y from 'yjs';
import {
    SafeDocumentContent,
    SafeSpace,
    AnyBlockItem,
    transformBlocksToSharedType,
    EmojiIcon,
    WeakDocumentContent,
} from '@saga/shared';

import { RealtimeStateWithDocument } from '@/components/RealtimeProvider';

import { BlocksLocation } from '../../../shared/src/SagaLocation';
import { Editor, Transforms } from 'slate';
import { changeWithEditor, safeDocumentContentFromYMap } from '../../../shared/src/SpaceOperations';
import { useCurrentWorkspace } from '@/components/WorkspaceContext';
import {
    useDocumentProperty,
    usePerformActionWithSafeContent as usePerformActionWithSafeContentBase,
    RealtimeDocumentPovider as BaseRealtimeDocumentPovider,
    useConnectedDocuments as useConnectedDocumentsBase,
    RealtimeDocumentContextType,
    useDocumentAwareness as useDocumentAwarenessBase,
    useDocumentContentRealtime as useDocumentContentRealtimeBase,
} from './BaseRealtimeDocumentProvider';

export const RealtimeDocumentContext = React.createContext<RealtimeDocumentContextType<SafeDocumentContent>>({
    loadDocument: () => Promise.resolve(undefined),
    unLoadDocument: () => {},
    documentStatesMap: {},
});
RealtimeDocumentContext.displayName = 'Document Provider';

function safeDocumentContentFromYMapConverter(state: RealtimeStateWithDocument) {
    return safeDocumentContentFromYMap(state.document.getMap('content'));
}

export function useDocumentYBlocks(documentId: string | undefined) {
    return useDocumentProperty<Y.Array<any>, WeakDocumentContent>(
        documentId,
        'blocks',
        safeDocumentContentFromYMapConverter,
        RealtimeDocumentContext,
    );
}

export function useDocumentIcon(documentId: string | undefined) {
    return useDocumentProperty<EmojiIcon, WeakDocumentContent>(
        documentId,
        'icon',
        safeDocumentContentFromYMapConverter,
        RealtimeDocumentContext,
    );
}

export function useConnectedDocuments() {
    return useConnectedDocumentsBase<WeakDocumentContent>(
        safeDocumentContentFromYMapConverter,
        RealtimeDocumentContext,
    );
}

export function useDocumentAwareness(documentId: string | undefined) {
    return useDocumentAwarenessBase(documentId, RealtimeDocumentContext);
}

export function useDocumentContentRealtime() {
    return useDocumentContentRealtimeBase<SafeDocumentContent>(RealtimeDocumentContext);
}

export function usePerformActionWithSafeContent() {
    return usePerformActionWithSafeContentBase<SafeDocumentContent>(RealtimeDocumentContext);
}

export function usePerformActionWithYBlocks() {
    const performActionWithSafeContent = usePerformActionWithSafeContent();

    return React.useCallback(
        async (location: BlocksLocation, action: (blocks: Y.Array<any>) => void) => {
            return performActionWithSafeContent(location, (content: SafeDocumentContent) => {
                const yArray = content.map.get('blocks') as Y.Array<any> | undefined;
                action(yArray ?? transformBlocksToSharedType([]));
            });
        },
        [performActionWithSafeContent],
    );
}

export function usePerformBlockChangeWithEditor() {
    const performActionWithBlocks = usePerformActionWithYBlocks();

    return React.useCallback(
        async (space: SafeSpace, location: BlocksLocation, change: (editor: Editor, blocks?: Y.Array<any>) => void) =>
            performActionWithBlocks(location, (blocks) => {
                changeWithEditor(space, blocks, (editor: Editor) => change(editor, blocks));
            }),
        [performActionWithBlocks],
    );
}

export function usePerformAppendBlock() {
    const performBlockChangeWithEditor = usePerformBlockChangeWithEditor();

    return React.useCallback(
        (space: SafeSpace, location: BlocksLocation, blocks: Array<AnyBlockItem>) =>
            performBlockChangeWithEditor(space, location, (editor) => {
                Transforms.insertNodes(editor, blocks, { at: [editor.children.length - 1] });
            }),
        [performBlockChangeWithEditor],
    );
}

export const SpaceRealtimeDocumentProvider = ({ children }: { children: React.ReactNode }) => {
    const { id } = useCurrentWorkspace();
    return <RealtimeDocumentPovider spaceId={id}>{children}</RealtimeDocumentPovider>;
};

export const RealtimeDocumentPovider = ({ children, spaceId }: { children: React.ReactNode; spaceId: string }) => {
    return (
        <BaseRealtimeDocumentPovider
            spaceId={spaceId}
            converter={safeDocumentContentFromYMapConverter}
            documentType="content"
            Context={RealtimeDocumentContext}
        >
            {children}
        </BaseRealtimeDocumentPovider>
    );
};
